import { Navigate, useParams } from 'react-router';
import PageShell from '@components/pages/PageShell';
import {
    InnovationHero,
    InnovationAbout,
    InnovationFeatures,
    InnovationCTA,
} from '@components/pages/InnovationSections';
import { TakeItForASpin } from '@components/pages/FocusSections';
import Newsletter from '@components/landing/Newsletter';
import { getInnovation, innovationSite } from '@/data/pages';
import Seo from '@components/Seo';
import JsonLd from '@components/JsonLd';
import { breadcrumbSchema } from '@/lib/schema';
import { PageSkeleton, useSiteLoading } from '@components/Skeleton';

/** Shared template for every product page under the Innovations menu. */
export default function Innovation() {
    const { slug } = useParams();
    const page = getInnovation(slug);

    const loading = useSiteLoading();

    // The pools are empty until the feed lands, so "no such page" is not a
    // conclusion that can be drawn yet — drawing it sent every programme,
    // product and project link to the 404, and a redirect cannot be undone.
    if (!page) {
        if (loading) {
            return (
                <PageShell>
                    <div className="bg-white pb-14 pt-[120px] lg:pb-20 lg:pt-[160px]">
                        <PageSkeleton />
                    </div>
                </PageShell>
            );
        }
        return <Navigate to="/404" replace />;
    }

    return (
        <PageShell>
            <JsonLd
                id="breadcrumb"
                data={breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'Innovations', path: '/#innovations' },
                    { name: page.label, path: `/innovations/${page.slug}` },
                ])}
            />
            <Seo title={page.label} description={page.intro} canonical={`/innovations/${page.slug}`} />
            <InnovationHero
                key={page.slug}
                titleLead={page.titleLead}
                titleBold={page.titleBold}
                intro={page.intro}
                logo={page.logo}
                label={page.label}
                href={innovationSite(page.slug)}
            />
            <InnovationAbout label={page.label} />
            <InnovationFeatures />
            <TakeItForASpin
                video={{
                    heading: 'Don’t just take our word for it – take it for a spin!',
                    copy: `${page.label} is already running. The quickest way to understand it is to watch someone use it end to end — the intake, the handoff, and what happens at the point a person would otherwise have given up.`,
                    thumbnail: '/focus/demo-video.webp',
                }}
            />
            <InnovationCTA label={page.label} href={innovationSite(page.slug)} />
            <Newsletter />
        </PageShell>
    );
}
